import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ArrowRightIcon, CheckIcon, ShieldCheckIcon, DownloadIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Reveal, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { HeroArcs } from '../components/sections/HeroVisual';
import { GOV_MODULES, GOV_AUDIENCES, GOV_GUARANTEES, GOV_PILOT_STEPS } from '../data/cavallaGov';
import { GOV_FAQS } from '../data/faqs';
import { Faqs } from '../components/sections/Faqs';
import { Seo } from '../components/Seo';
import { serviceSchema, faqSchema, breadcrumbSchema, videoSchema } from '../lib/seo';
import { submitLead, requestDownloadUrl } from '../lib/leads';
import { track } from '../lib/analytics';
const OVERVIEW_FILE = 'cavalla-gov-overview.pdf';

/** The product walkthrough, hosted on the CDN. */
const DEMO_VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/dUw9vSiWji7kC2EeW7j7nN/cavalla-gov-demo.mp4';
const GOV_DESCRIPTION = 'Cavalla Gov gives a ministry of finance one live picture of how the national development plan is being delivered, and where development partner funding lands against it.';
const GOV_SCHEMA = [serviceSchema({
  name: 'Cavalla Gov',
  description: GOV_DESCRIPTION,
  path: '/cavalla-gov',
  serviceType: 'National plan delivery platform for government',
  audience: 'Ministries of finance and planning, delivery agencies, development partners',
  offers: GOV_MODULES.map(module => module.name)
}), videoSchema({
  name: 'Cavalla Gov in action',
  description: 'A walkthrough of the three Cavalla Gov modules: Delivery Tracker, Development Finance Intelligence and AI Reporting.',
  contentUrl: 'https://cdn.magicpatterns.com/uploads/dUw9vSiWji7kC2EeW7j7nN/cavalla-gov-demo.mp4',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/nTjBqcuuXgbjSwDzeQVhBn/Cavalla.png',
  path: '/cavalla-gov'
}), faqSchema(GOV_FAQS), breadcrumbSchema([{
  name: 'Home',
  path: '/'
}, {
  name: 'Cavalla Gov',
  path: '/cavalla-gov'
}])];
export function CavallaGov() {
  const [name, setName] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [role, setRole] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [wantsOverview, setWantsOverview] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [overviewUrl, setOverviewUrl] = React.useState<string | null>(null);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName: name,
      organisation,
      role,
      country,
      consent,
      source: 'cavalla-gov',
      pipeline: 'government',
      notes: `Cavalla Gov briefing request. Role: ${role || 'not given'}. Country: ${country || 'not given'}.`
    });
    if (!result.ok) {
      setSubmitting(false);
      setError(result.error ?? 'We could not send your request. Please try again.');
      return;
    }
    if (wantsOverview) {
      const download = await requestDownloadUrl(OVERVIEW_FILE, {
        kind: 'resource',
        id: 'cavalla-gov'
      });
      if (download.url) {
        setOverviewUrl(download.url);
        track('ebook_download', {
          resource: 'cavalla-gov-overview'
        });
      }
    }
    setSubmitting(false);
    setSent(true);
  };
  return <>
      <Seo title="Cavalla Gov, national plan delivery platform for government" description="Cavalla Gov gives a ministry of finance one live picture of national plan delivery across every agency, and shows where development partner funding lands against it." path="/cavalla-gov" jsonLd={GOV_SCHEMA} data-id="element-702" data-mp="2vnumqt2x4j5g" />

      {/* Hero */}
      <section className="surface-dark relative isolate overflow-hidden" data-id="element-703" data-mp="2kzzxzfrslld9">
        <HeroArcs className="pointer-events-none absolute -right-12 top-0 h-[380px] w-[380px] opacity-80" data-id="element-704" data-mp="2fz2dgwjb8f8l" />
        <Container className="relative py-14 sm:py-20" data-id="element-705" data-mp="2byry4t9moch2">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-706" data-mp="2c0csahs15tee">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-707" data-mp="26zp3m2r9xonc">
              <li data-id="element-708" data-mp="2275qf6wi0vj2">
                <Link to="/" className="hover:text-white" data-id="element-709" data-mp="226matwubmj87">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-710" data-mp="1x71615sgzgnk">
                <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-711" data-mp="1saj5ki46a4h1" />
              </li>
              <li aria-current="page" className="text-white/80" data-id="element-712" data-mp="1o8frrmu16nsl">
                Cavalla Gov
              </li>
            </ol>
          </nav>

          <Kicker data-id="element-713" data-mp="1jans26n59xdu">Cavalla Gov</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[34px] font-light leading-tight text-white sm:text-[50px]" data-id="element-714" data-mp="1jbp4aefwcpx5">
            One shared view of national plan delivery, built for government.
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-715" data-mp="7ryxz6da6fbr">
            We give a ministry of finance one live picture of how the national development plan is being
            delivered, and where development partner funding lands against it.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-716" data-mp="3g7s2sny922cz">
            <Button href="#briefing" size="lg" shape="pill" data-id="element-717" data-mp="3g8bj60894iow">
              Request a briefing
            </Button>
            <Button href="#platform" variant="reversed" size="lg" shape="pill" data-id="element-718" data-mp="3l4s8uyv3n543">
              See how it works
            </Button>
          </div>
          <p className="mt-8 text-small text-white/55" data-id="element-719" data-mp="2sizf1ers70tg">
            Cavalla Gov is in pilot. It draws on our three advisory lines, and is offered as a platform in its
            own right.
          </p>
        </Container>
      </section>

      {/* The problem */}
      <Section aria-labelledby="problem" data-id="element-720" data-mp="4kha5pdkzlsf">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-721" data-mp="3vs25s0q8jcln">
          <div data-id="element-722" data-mp="devyehg3a6no">
            <SectionHeader id="problem" kicker="Why now" title="Governments publish ambitious plans, then cannot see what is delivered." data-id="element-723" data-mp="dfnlt4zj8y2v" />
            <div className="mt-6 max-w-prose space-y-5 text-[16px] text-body" data-id="element-724" data-mp="3hsu2avn1hibn">
              <p data-id="element-725" data-mp="3cu92uyfm32o9">
                A national development plan can run to hundreds of commitments across dozens of agencies.
                Without one shared system, progress is reported late, unevenly, or not at all.
              </p>
              <p data-id="element-726" data-mp="3ltw51mg60cid">
                In one recent first year of delivery, a third of a country&rsquo;s plan interventions could not
                be rated because agencies had not reported. That gap, not the plan itself, is what puts a
                government&rsquo;s credibility with its own Cabinet and its donors at risk.
              </p>
            </div>
          </div>

          <div className="rounded-md border border-line bg-mist p-7 sm:p-8" data-id="element-727" data-mp="1dntxvszrokzk">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-728" data-mp="znj68nqmv2dc">The reporting gap</p>
            <p className="mt-6 font-display text-[64px] leading-none text-royal" data-id="element-729" data-mp="14mdyegahq3a4">1 in 3</p>
            <p className="mt-4 max-w-sm text-[16px] text-body" data-id="element-730" data-mp="1sc89asb91hrq">
              Plan interventions that could not be rated in a recent first year of delivery, because the
              reporting never arrived.
            </p>
            <p className="mt-5 text-small text-muted" data-id="element-731" data-mp="1imx25b7xan6h">
              This is the problem Cavalla Gov is built to close. It is a reporting problem before it is a
              delivery problem.
            </p>
          </div>
        </div>
      </Section>

      {/* The platform */}
      <Section id="platform" tone="mist" aria-labelledby="platform-heading" data-id="element-732" data-mp="2g1j3fqy3gdww">
        <SectionHeader id="platform-heading" kicker="The platform" title="Three modules, one shared record of the plan." lead="Each module stands on the data the one before it produces, so a ministry is never asked to maintain two versions of the truth." data-id="element-733" data-mp="2g3vrmlug3kie" />

        <div className="mt-12 grid gap-px bg-line lg:grid-cols-3" data-id="element-734" data-mp="67drgh5yc14g">
          {GOV_MODULES.map((module, index) => <Reveal key={module.id} delay={index * 0.06} className="h-full" data-id="element-735" data-mp="yy5kx6vmubo3" data-mpi={index}>
              <article className="flex h-full flex-col bg-panel p-8" data-id="element-736" data-mp="yw1iyuhanmqg">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-737" data-mp="tzubgx99236e">
                  Module {index + 1}
                </p>
                <h3 className="mt-4 font-display text-[26px] leading-tight text-ink" data-id="element-738" data-mp="kz5xu3nrq4u1">{module.name}</h3>
                <p className="mt-3 text-[16px] font-medium text-royal" data-id="element-739" data-mp="3aohpip0e14v0">{module.promise}</p>
                <p className="mt-4 text-[16px] text-body" data-id="element-740" data-mp="35p51i0xs5rqu">{module.detail}</p>
                <ul className="mt-6 space-y-3 text-[15px] text-body" data-id="element-741" data-mp="35r80vbktox88">
                  {module.points.map(point => <li key={point} className="flex gap-3" data-id="element-742" data-mp="1q5nwm2zebwqd">
                      <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-743" data-mp="1q3tcgqvy88nc" />
                      {point}
                    </li>)}
                </ul>
              </article>
            </Reveal>)}
        </div>

        <Callout className="mt-10 max-w-3xl" data-id="element-744" data-mp="23w9999y9baft">
          <span className="flex gap-3" data-id="element-745" data-mp="23zop4ezr8nia">
            <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-746" data-mp="28vm7equkw3xp" />
            <span data-id="element-747" data-mp="2dv8jgwyo7e4x">
              Where the platform uses AI, it drafts and it flags. Every AI drafted output is labelled as AI
              generated and is reviewed and approved by a named official before it leaves the platform. No
              automated decision is taken about an individual citizen.
            </span>
          </span>
        </Callout>
      </Section>

      {/* Demo */}
      <Section aria-labelledby="demo" data-id="element-748" data-mp="40ujw5r5lnun">
        <SectionHeader id="demo" kicker="Cavalla Gov in action" title="See the platform running." lead="A short walkthrough of the three modules, recorded on the platform itself rather than on slides." data-id="element-749" data-mp="41xbkfwavubm" />
        <figure className="mt-10" data-id="element-750" data-mp="hzubnm9yqiv1">
          <div className="overflow-hidden rounded-md border border-line bg-panel" data-id="element-751" data-mp="i0wx3eoint3k">
            <video controls preload="metadata" playsInline className="block aspect-video w-full bg-charcoal" aria-describedby="demo-caption" data-id="element-752" data-mp="n001qy0l3xb0">
              <source src={DEMO_VIDEO_URL} type="video/mp4" data-id="element-753" data-mp="vs226wanldla" />
              Your browser cannot play embedded video.{' '}
              <a href={DEMO_VIDEO_URL} className="underline" data-id="element-754" data-mp="10spurmej62hm">
                Download the walkthrough
              </a>{' '}
              instead.
            </video>
          </div>
          <figcaption id="demo-caption" className="mt-4 max-w-prose text-small text-muted" data-id="element-755" data-mp="1ap17gdi18ucl">
            The walkthrough does not play on its own, so it costs you nothing until you press play. Figures shown
            are sample data, not a live government record. If you would prefer to see it running on your own
            plan, request a briefing below.
          </figcaption>
        </figure>
      </Section>

      {/* Who it is for */}
      <Section aria-labelledby="audience" data-id="element-756" data-mp="3dx1tlghg2cha">
        <SectionHeader id="audience" kicker="Who it is for" title="Built for the people accountable for the plan." data-id="element-757" data-mp="3dws2fpknxza0" />
        <div className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-758" data-mp="3478kcm4ry9g7">
          {GOV_AUDIENCES.map(audience => <div key={audience.name} className="border-t border-line pt-6" data-id="element-759" data-mp="2z8yosaf516u2">
              <h3 className="font-display text-[22px] leading-snug text-ink" data-id="element-760" data-mp="iexn3o9mzxf">{audience.name}</h3>
              <p className="mt-3 text-[16px] text-body" data-id="element-761" data-mp="3rx2uux1mflxa">{audience.need}</p>
            </div>)}
        </div>
      </Section>

      {/* How we work */}
      <Section tone="mist" aria-labelledby="how-we-work" data-id="element-762" data-mp="1q02eyd631jh0">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]" data-id="element-763" data-mp="1uz5932kiy5a7">
          <div className="lg:sticky lg:top-32 lg:self-start" data-id="element-764" data-mp="1v0q1aljd9c4w">
            <SectionHeader id="how-we-work" kicker="How we work" title="A pilot first, then a platform." data-id="element-765" data-mp="1g8xzzdgepym6" />
            <p className="mt-6 max-w-md text-[16px] text-body" data-id="element-766" data-mp="1l7iz19jxac91">
              We start with one ministry and a small set of agencies, usually financed as development partner
              technical assistance rather than a direct purchase, then scale across agencies and, over time,
              across countries. We quote public sector work on a custom basis.
            </p>
          </div>

          <div data-id="element-767" data-mp="12arbgnxclaz6">
            <ol className="divide-y divide-line border-t border-line" data-id="element-768" data-mp="12a84215bq4ze">
              {GOV_PILOT_STEPS.map((phase, index) => <li key={phase.step} className="grid gap-4 py-7 sm:grid-cols-[56px_1fr]" data-id="element-769" data-mp="2wnz8mm8spynz" data-mpi={index}>
                  <span className="font-display text-[24px] leading-none text-gold" data-id="element-770" data-mp="2wlmsmemllydk">0{index + 1}</span>
                  <div data-id="element-771" data-mp="kyb0zm14wifu">
                    <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-772" data-mp="g22mmaurgt9q">{phase.step}</h3>
                    <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-773" data-mp="uqyuo7wti6ac">{phase.detail}</p>
                  </div>
                </li>)}
            </ol>

            <div className="mt-10 rounded-md border border-line bg-panel p-7" data-id="element-774" data-mp="pxvvnk99u4mr">
              <h3 className="font-display text-[20px] text-ink" data-id="element-775" data-mp="13paik26aupz3">What the government controls</h3>
              <ul className="mt-5 space-y-3 text-[15px] text-body" data-id="element-776" data-mp="yrshsz2frh26">
                {GOV_GUARANTEES.map(item => <li key={item} className="flex gap-3" data-id="element-777" data-mp="1dmyprrl3dpy5">
                    <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-778" data-mp="18rjdv56na44d" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Why Cavalla */}
      <Section aria-labelledby="why-cavalla" data-id="element-779" data-mp="21l62asr7ewg9">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr]" data-id="element-780" data-mp="21k4tkuxnum1p">
          <div data-id="element-781" data-mp="26iy208y3bmv0">
            <SectionHeader id="why-cavalla" kicker="Why Cavalla" title="Based in Africa, fluent in its plans and its constraints." data-id="element-782" data-mp="26ifxkp19y2c0" />
            <div className="mt-6 max-w-prose space-y-5 text-[16px] text-body" data-id="element-783" data-mp="2fje7tp2b3631">
              <p data-id="element-784" data-mp="1iuon7qzanzdd">
                Cavalla Gov reuses the AI matching engine we built for grant discovery, redirected from matching
                organisations to funding toward matching government programmes to development finance.
              </p>
              <p data-id="element-785" data-mp="1rtjrgxis2ze1">
                We build for a mid range phone on a slow connection, because that is how most agency officers
                will reach it. And we sit inside this market, not outside it, which is how a platform earns the
                trust that distant vendors cannot.
              </p>
            </div>
            <Link to="/services" className="mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-786" data-mp="wlrakqz1qrxy">
              The advisory lines behind the platform
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-787" data-mp="njqg21v76jzz" />
            </Link>
          </div>

          <div className="rounded-md border border-line bg-mist p-7 sm:p-8" data-id="element-788" data-mp="il6vo5w95g9q">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-789" data-mp="dn5droakbmzu">Where it draws from</p>
            <dl className="mt-6 space-y-6 text-[16px]" data-id="element-790" data-mp="8vdij0ggot3d">
              <div data-id="element-791" data-mp="3wsstuyu5rt6">
                <dt className="font-medium text-ink" data-id="element-792" data-mp="3waoeb20s7a6">Cavalla Intelligence</dt>
                <dd className="mt-1 text-body" data-id="element-793" data-mp="3w14rw8jo3ify">Indicator baselines and the sector view behind each pillar.</dd>
              </div>
              <div data-id="element-794" data-mp="3r0qhijulfwer">
                <dt className="font-medium text-ink" data-id="element-795" data-mp="3r18p2cmphx1z">Cavalla Growth</dt>
                <dd className="mt-1 text-body" data-id="element-796" data-mp="3m2f8ucg1bry4">
                  Development finance mapping, and the case a ministry puts to its partners.
                </dd>
              </div>
              <div data-id="element-797" data-mp="3hdw8wlk4a8tf">
                <dt className="font-medium text-ink" data-id="element-798" data-mp="3hf7emqx4hqn6">Cavalla AI</dt>
                <dd className="mt-1 text-body" data-id="element-799" data-mp="32h59icag0x3t">
                  The drafting and early warning layer, with disclosure and human review built in.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </Section>

      {/* Questions */}
      <Faqs id="gov-faqs" kicker="Questions we are asked" title="What a ministry asks before it says yes." items={GOV_FAQS} data-id="element-800" data-mp="3v7nly3x24atw" />

      {/* Lead capture */}
      <Section id="briefing" tone="mist" aria-labelledby="briefing-heading" data-id="element-801" data-mp="3lfhl04otuj2k">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]" data-id="element-802" data-mp="3qdjd1f48dsq6">
          <div data-id="element-803" data-mp="hyiw9hafy6hr">
            <SectionHeader id="briefing-heading" kicker="Request a briefing" title="Thirty minutes, on your own plan." lead="Tell us which plan you are accountable for and we will show the platform running against its structure, not a generic demonstration." data-id="element-804" data-mp="hysq5fylwxsq" />
            <p className="mt-6 max-w-md text-[15px] text-muted" data-id="element-805" data-mp="22gjwg69b6pn9">
              We reply within two working days. Your details reach our team only, and personal data is held in
              the European Union.
            </p>
          </div>

          <div className="rounded-md border border-line bg-panel p-6 sm:p-8" data-id="element-806" data-mp="1oh24ji0puw9o">
            {sent ? <div role="status" data-id="element-807" data-mp="1jih4pjpdmelr">
                <div className="flex items-center gap-2 text-gold" data-id="element-808" data-mp="1jjirglfr8pjb">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-809" data-mp="1xii56y1qbk0p" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-810" data-mp="1xfnl57mmo17t">Request received</span>
                </div>
                <h3 className="mt-4 font-display text-[24px] leading-snug text-ink" data-id="element-811" data-mp="38wm6kpancafs">
                  Thank you. We will be in touch within two working days.
                </h3>
                <p className="mt-3 text-[16px] text-body" data-id="element-812" data-mp="128wn3rq5lkk0">
                  A confirmation is on its way to {email} once email delivery is connected. If your briefing is
                  time sensitive, say so in your reply and we will bring it forward.
                </p>
                {overviewUrl && <a href={overviewUrl} className="mt-7 inline-flex items-center gap-2 rounded-sm bg-gold px-5 py-3 text-[15px] font-medium text-charcoal transition-colors hover:bg-[#ff7440]" data-id="element-813" data-mp="1gyb0dawuom4o">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-814" data-mp="1pwmpt9lzddvj" />
                    Download the one page overview
                  </a>}
                {overviewUrl && <p className="mt-3 text-small text-muted" data-id="element-815" data-mp="1zvvogfrsjgo8">
                    This link is signed for your request and expires after fifteen minutes.
                  </p>}
              </div> : <form onSubmit={onSubmit} className="space-y-5" noValidate data-id="element-816" data-mp="2isvwpruyy64f">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-817" data-mp="2iv096y6xodgb">
                  <Field id="gov-name" label="Name" required data-id="element-818" data-mp="2e14dy3i214ou">
                    <input id="gov-name" type="text" required value={name} onChange={event => setName(event.target.value)} className={inputClass} data-id="element-819" data-mp="2a0lnodvt86or" />
                  </Field>
                  <Field id="gov-org" label="Organisation" required data-id="element-820" data-mp="2xpwqe0wq0b5j">
                    <input id="gov-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-821" data-mp="2sr39dyi8620m" />
                  </Field>
                </div>

                <div className="grid gap-5 sm:grid-cols-2" data-id="element-822" data-mp="1cbihn5og97j7">
                  <Field id="gov-role" label="Ministry or role" required data-id="element-823" data-mp="1o06xfhovxdk6">
                    <input id="gov-role" type="text" required value={role} onChange={event => setRole(event.target.value)} placeholder="Ministry of Finance, M and E" className={inputClass} data-id="element-824" data-mp="1j9i8a90ogso7" />
                  </Field>
                  <Field id="gov-country" label="Country" required data-id="element-825" data-mp="21zq6ccpissxj">
                    <input id="gov-country" type="text" required value={country} onChange={event => setCountry(event.target.value)} className={inputClass} data-id="element-826" data-mp="220hpup5uf07g" />
                  </Field>
                </div>

                <Field id="gov-email" label="Work email" required error={error ?? undefined} data-id="element-827" data-mp="gm3viyybjz4n">
                  <input id="gov-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@ministry.gov" aria-describedby={error ? 'gov-email-error' : undefined} className={inputClass} data-id="element-828" data-mp="2sa8noas5f8c2" />
                </Field>

                <ConsentCheckbox id="gov-overview" checked={wantsOverview} onChange={setWantsOverview} data-id="element-829" data-mp="3bxg5kiki2vuw">
                  Send me the one page Cavalla Gov overview as well.
                </ConsentCheckbox>

                <ConsentCheckbox id="gov-consent" checked={consent} onChange={setConsent} data-id="element-830" data-mp="3kwbc5vrgi7yq">
                  I am happy for Cavalla Solution to contact me about Cavalla Gov. See our{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-831" data-mp="1qly0lsardb4x">
                    privacy notice
                  </Link>
                  .
                </ConsentCheckbox>

                <Button type="submit" size="lg" disabled={submitting} data-id="element-832" data-mp="1nj1vl19q0nlg">
                  {submitting ? 'Sending' : 'Request a briefing'}
                </Button>
              </form>}
          </div>
        </div>
      </Section>

      {/* Closing call to action */}
      <section className="surface-dark relative isolate overflow-hidden" data-id="element-833" data-mp="1sgtkdmf2jtlf">
        <HeroArcs className="pointer-events-none absolute -right-16 -top-8 h-[300px] w-[300px] opacity-70" data-id="element-834" data-mp="26h4d6uzxvi98" />
        <Container className="relative py-16 lg:py-20" data-id="element-835" data-mp="22gu9xq3j75y2">
          <div className="flex flex-col items-start justify-between gap-9 lg:flex-row lg:items-center" data-id="element-836" data-mp="22e82dqgn4qfu">
            <div className="max-w-xl" data-id="element-837" data-mp="2g9a4e2id9vnd">
              <h2 className="font-display text-[30px] font-light leading-tight text-white sm:text-[40px]" data-id="element-838" data-mp="2b82zqcbitckx">
                Bring this to your ministry.
              </h2>
              <p className="mt-5 text-[17px] text-white/72" data-id="element-839" data-mp="2l6sgg1rokqzl">
                Book a short briefing and we will show you the platform running on your own plan.
              </p>
            </div>
            <Button href="#briefing" size="lg" shape="pill" className="shrink-0" data-id="element-840" data-mp="38txd6vc31f2e">
              Request a briefing
            </Button>
          </div>
        </Container>
      </section>
    </>;
}